import { useState } from "react";
import { Link } from "react-router";
import { useLocale, useTranslations } from "use-intl";
import { CheckCircle2, ChevronRight, Copy } from "lucide-react";
import { lt } from "@liftquest/shared/localized";
import { PLAN_TEMPLATES } from "@liftquest/shared/templates";
import { Page, PageHeader } from "@/components/app/page-header";
import { PendingButton } from "@/components/app/pending-button";
import { PageLoading } from "@/components/app/states";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { duplicatePlan, setActivePlan } from "@/lib/actions";
import { useMe, usePlans } from "@/lib/queries";
import { NewPlanButton } from "./new-plan-button";

export function PlansPage() {
  const { profile } = useMe().data!;
  const locale = useLocale();
  const t = useTranslations("plans");
  const tc = useTranslations("common");
  const plans = usePlans();
  const [pending, setPending] = useState<string | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setPending(key);
    try {
      await fn();
    } finally {
      setPending(null);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("title")}
        actions={
          <NewPlanButton
            templates={PLAN_TEMPLATES.map((p) => ({
              key: p.key,
              name: lt(p.name, locale),
              description: lt(p.description, locale),
            }))}
          />
        }
      />
      {!plans.data ? (
        <PageLoading />
      ) : (
        <ul className="mt-5 space-y-3">
          {plans.data.map((p) => {
            const active = profile.activePlanId === p.id;
            const exercises = p.workouts.reduce((n, w) => n + w.exercises.length, 0);
            return (
              <li key={p.id}>
                <Card className={active ? "gap-0 border-primary/50 py-0" : "gap-0 py-0"}>
                  <Link to={`/plans/${p.id}`} className="flex items-start gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate font-semibold">{lt(p.name, locale)}</h2>
                        {active && (
                          <Badge className="shrink-0">
                            <CheckCircle2 /> {t("active")}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{lt(p.description, locale)}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {p.workouts.map((w) => lt(w.name, locale)).join(" · ")} · {tc("exercises", { count: exercises })}
                      </p>
                    </div>
                    <ChevronRight className="mt-1 size-5 shrink-0 text-muted-foreground" />
                  </Link>
                  <div className="flex gap-2 border-t px-4 py-2.5">
                    {!active && (
                      <PendingButton size="sm" pending={pending === `a-${p.id}`} onClick={() => run(`a-${p.id}`, () => setActivePlan(p.id))}>
                        {t("setActive")}
                      </PendingButton>
                    )}
                    <PendingButton
                      size="sm"
                      variant="ghost"
                      pending={pending === `d-${p.id}`}
                      onClick={() => run(`d-${p.id}`, () => duplicatePlan(p.id, t("copySuffix")))}
                    >
                      <Copy /> {t("duplicate")}
                    </PendingButton>
                  </div>
                </Card>
              </li>
            );
          })}
          {plans.data.length === 0 && <p className="text-sm text-muted-foreground">{t("empty")}</p>}
        </ul>
      )}
    </Page>
  );
}
